import { useState } from 'react'
import { supabase } from './lib/supabaseClient'

function Blog() {
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedPost, setSelectedPost] = useState(null)
  const [newsletterEmail, setNewsletterEmail] = useState('')
  const [newsletterMsg, setNewsletterMsg] = useState('')

  const blogPosts = [
    {
      id: 1,
      title: 'How to Generate More Real Estate Leads',
      date: 'May 10, 2025',
      category: 'Lead Generation',
      readTime: '5 min read',
      image: '🎯',
      excerpt: 'Discover proven strategies to attract qualified leads and convert them into loyal clients. Learn about digital marketing, social media, and networking techniques.',
      fullContent: `
        <h2>Introduction</h2>
        <p>Generating quality leads is the lifeblood of any successful real estate business. In today's competitive market, traditional methods alone are no longer enough. This guide will show you proven strategies to attract, nurture, and convert more leads.</p>
        
        <h3>1. Leverage Social Media Platforms</h3>
        <p>Instagram, Facebook, and LinkedIn are powerful tools for reaching potential buyers and sellers. Share property videos, virtual tours, and client testimonials. Use targeted ads to reach specific demographics in your area.</p>
        
        <h3>2. Optimize Your Website for SEO</h3>
        <p>Most home buyers start their search online. Ensure your website ranks for local keywords like "homes for sale in [your city]" and "real estate agent near me." Create valuable content that answers common buyer questions.</p>
        
        <h3>3. Build an Email List</h3>
        <p>Collect emails through your website and social media. Send weekly newsletters with new listings, market updates, and helpful tips. Segment your list based on buyer/seller interests.</p>
        
        <h3>4. Host Open Houses and Events</h3>
        <p>Nothing beats face-to-face interaction. Host regular open houses, first-time buyer seminars, and community events. Collect contact information from every attendee.</p>
        
        <h3>5. Ask for Referrals</h3>
        <p>Your happiest clients are your best marketing channel. Create a referral program that rewards clients who send friends and family your way.</p>
        
        <h2>Conclusion</h2>
        <p>Consistency is key. Implement these strategies daily, track your results, and adjust what works best for your market. With EstatesFlow CRM, you can track all your leads in one place and never miss a follow-up.</p>
      `,
      author: 'Sarah Johnson',
      authorRole: 'Head of Product',
      authorBio: 'Sarah has 8 years of real estate experience and has helped over 500 agents grow their business.'
    },
    {
      id: 2,
      title: 'The Future of Property Management Software',
      date: 'May 5, 2025',
      category: 'Technology',
      readTime: '4 min read',
      image: '🤖',
      excerpt: 'AI, automation, and predictive analytics are transforming how properties are managed. See what\'s coming next in real estate tech.',
      fullContent: `
        <h2>The Property Management Revolution</h2>
        <p>The property management industry is undergoing a massive transformation driven by artificial intelligence, automation, and data analytics. Here's what's coming.</p>
        
        <h3>AI-Powered Tenant Screening</h3>
        <p>Machine learning algorithms can now predict tenant reliability with unprecedented accuracy, reducing bad debt and eviction rates by up to 60%.</p>
        
        <h3>Automated Maintenance Requests</h3>
        <p>Smart systems can detect issues before tenants report them - from leak detection to HVAC failures. Automated workflows dispatch the right vendors instantly.</p>
        
        <h3>Predictive Rent Pricing</h3>
        <p>AI analyzes market trends, seasonality, and local demand to recommend optimal rental prices, maximizing your revenue while minimizing vacancy.</p>
        
        <h3>Virtual Property Tours</h3>
        <p>3D virtual tours and VR walkthroughs allow prospective tenants to view properties from anywhere, expanding your reach to out-of-town renters.</p>
        
        <h3>Integrated Financial Management</h3>
        <p>Modern platforms like EstatesFlow combine rent collection, expense tracking, owner payouts, and tax reporting in one seamless system.</p>
        
        <h2>What This Means for You</h2>
        <p>Property managers who embrace these technologies will gain a significant competitive advantage - lower costs, higher occupancy, and happier owners and tenants.</p>
      `,
      author: 'Michael Chen',
      authorRole: 'CTO',
      authorBio: 'Michael is a cloud architecture expert with 12 years of experience building scalable SaaS platforms.'
    },
    {
      id: 3,
      title: 'Real Estate Market Trends 2025',
      date: 'Apr 28, 2025',
      category: 'Market Insights',
      readTime: '6 min read',
      image: '📈',
      excerpt: 'Analysis of current market conditions, pricing trends, and what real estate professionals need to know for the coming year.',
      fullContent: `
        <h2>2025 Real Estate Market Overview</h2>
        <p>As we move through 2025, several key trends are shaping the real estate landscape across USD, EUR, and NGN markets.</p>
        
        <h3>Interest Rate Stabilization</h3>
        <p>After years of volatility, interest rates are showing signs of stabilization, encouraging both buyers and sellers to re-enter the market.</p>
        
        <h3>Rise of Secondary Cities</h3>
        <p>Remote work continues to drive demand in secondary and tertiary cities. Properties in mid-sized cities are seeing faster appreciation than major metros.</p>
        
        <h3>Sustainable Living</h3>
        <p>Energy-efficient homes with solar panels, smart systems, and sustainable materials command premium prices and sell faster.</p>
        
        <h3>Multifamily Investment Boom</h3>
        <p>Investors are increasingly turning to multifamily properties for stable cash flow and appreciation, driven by housing affordability challenges.</p>
        
        <h3>Technology Adoption</h3>
        <p>CRMs like EstatesFlow are becoming essential tools, with agents who use technology closing 40% more deals than those who don't.</p>
        
        <h2>Regional Highlights</h2>
        <p><strong>United States:</strong> Sun Belt cities continue to attract residents. Florida and Texas lead in job growth and housing demand.</p>
        <p><strong>Europe:</strong> Berlin, Lisbon, and Athens offer attractive investment opportunities with growing tech sectors.</p>
        <p><strong>Nigeria:</strong> Lagos and Abuja remain hotspots, with luxury developments and affordable housing projects gaining momentum.</p>
      `,
      author: 'Rizqah Olajide Adedokun',
      authorRole: 'CEO & Founder',
      authorBio: 'Raola has 5+ years of real estate technology experience and has helped launch multiple successful proptech startups.'
    },
    {
      id: 4,
      title: 'Maximizing ROI with Data Analytics',
      date: 'Apr 20, 2025',
      category: 'Analytics',
      readTime: '7 min read',
      image: '📊',
      excerpt: 'How to use data to make better investment decisions, optimize pricing, and improve client satisfaction.',
      fullContent: `
        <h2>Data-Driven Real Estate</h2>
        <p>In today's market, gut feelings aren't enough. Data analytics provides the insights you need to make smarter decisions and maximize returns.</p>
        
        <h3>Lead Scoring</h3>
        <p>Not all leads are equal. Use data to identify which prospects are most likely to convert, then focus your energy where it matters most. EstatesFlow's lead scoring analyzes behavior, engagement, and demographics.</p>
        
        <h3>Pricing Optimization</h3>
        <p>Analyze comparable sales, time-on-market data, and buyer demand to price properties for maximum return while minimizing days on market.</p>
        
        <h3>Marketing Attribution</h3>
        <p>Which marketing channels deliver the best ROI? Track every lead's source and calculate cost-per-acquisition for each channel. Allocate your budget to what works.</p>
        
        <h3>Client Lifetime Value</h3>
        <p>Understanding CLV helps you determine how much to invest in acquisition and retention. Top agents earn 60% of their revenue from repeat clients and referrals.</p>
        
        <h3>Market Forecasting</h3>
        <p>Predict which neighborhoods will appreciate fastest, identify emerging hotspots, and time your investments strategically.</p>
        
        <h2>Getting Started with Analytics</h2>
        <p>Start with the basics: track everything. Use EstatesFlow's built-in analytics dashboards to monitor key metrics without complex setup.</p>
      `,
      author: 'Amara Okonkwo',
      authorRole: 'Customer Success',
      authorBio: 'Amara specializes in helping real estate professionals leverage data to grow their businesses.'
    }
  ]

  const categories = ['all', ...new Set(blogPosts.map(p => p.category))]

  const filteredPosts = selectedCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(p => p.category === selectedCategory)

  const handleNewsletter = async () => {
    if (!newsletterEmail) {
      setNewsletterMsg('Please enter your email')
      return
    }
    
    const { error } = await supabase.from('newsletter_subscribers').upsert({
      email: newsletterEmail,
      subscribed_at: new Date(),
      source: 'blog'
    })
    
    if (error) {
      setNewsletterMsg('Error: ' + error.message)
    } else {
      setNewsletterMsg('✅ Subscribed successfully!')
      setNewsletterEmail('')
      setTimeout(() => setNewsletterMsg(''), 3000)
    }
  }

  if (selectedPost) {
    return (
      <div style={containerStyle}>
        <button onClick={() => setSelectedPost(null)} style={backButton}>← Back to All Articles</button>
        
        <div style={articleContainer}>
          <div style={articleHeader}>
            <div style={articleImage}>{selectedPost.image}</div>
            <h1 style={articleTitle}>{selectedPost.title}</h1>
            <div style={articleMeta}>
              <span>📅 {selectedPost.date}</span>
              <span>⏱️ {selectedPost.readTime}</span>
              <span>📂 {selectedPost.category}</span>
            </div>
          </div>
          
          <div style={articleContent} dangerouslySetInnerHTML={{ __html: selectedPost.fullContent }} />
          
          <div style={authorSection}>
            <div style={authorAvatar}>{selectedPost.image}</div>
            <div style={authorInfo}>
              <h3>{selectedPost.author}</h3>
              <p>{selectedPost.authorRole}</p>
              <p style={authorBio}>{selectedPost.authorBio}</p>
            </div>
          </div>
          
          <div style={shareSection}>
            <h4>Share this article:</h4>
            <div style={shareButtons}>
              <button style={shareBtn}>📧 Email</button>
              <button style={shareBtn}>📘 Facebook</button>
              <button style={shareBtn}>🐦 Twitter</button>
              <button style={shareBtn}>💼 LinkedIn</button>
              <button style={shareBtn}>📱 WhatsApp</button>
            </div>
          </div>
        </div>
        
        <div style={relatedSection}>
          <h3>Related Articles</h3>
          <div style={relatedGrid}>
            {blogPosts.filter(p => p.id !== selectedPost.id && p.category === selectedPost.category).slice(0, 2).map(post => (
              <div key={post.id} style={relatedCard} onClick={() => setSelectedPost(post)}>
                <div style={relatedImage}>{post.image}</div>
                <h4>{post.title}</h4>
                <p>{post.excerpt.substring(0, 80)}...</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <div style={footerContent}>
            <div>
              <h3>🏢 EstatesFlow</h3>
              <p>Complete Real Estate Management</p>
            </div>
            <div style={footerLinks}>
              <button onClick={() => window.location.href = '/'} style={footerLink}>Home</button>
              <button onClick={() => window.location.href = '/about'} style={footerLink}>About</button>
              <button onClick={() => window.location.href = '/blog'} style={footerLink}>Blog</button>
              <button onClick={() => window.location.href = '/#contact'} style={footerLink}>Contact</button>
            </div>
          </div>
          <p>© 2025 EstatesFlow CRM. All rights reserved.</p>
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyle}>
      {/* Hero Section */}
      <div style={heroStyle}>
        <h1 style={heroTitle}>Resources & Insights</h1>
        <p style={heroSubtitle}>Expert advice, market trends, and best practices for real estate professionals</p>
      </div>

      <div style={homeButtonContainer}>
        <button onClick={() => window.location.href = '/'} style={homeButton}>← Back to Home</button>
          </div>

      {/* Category Filter */}
      <div style={categoryBar}>
        <div style={categoryContainer}>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              style={selectedCategory === cat ? activeCategory : categoryBtn}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Blog Grid */}
      <div style={blogGrid}>
        {filteredPosts.map(post => (
          <div key={post.id} style={blogCard} onClick={() => setSelectedPost(post)}>
            <div style={blogImage}>{post.image}</div>
            <div style={blogCategory}>{post.category}</div>
            <h3 style={blogTitle}>{post.title}</h3>
            <p style={blogExcerpt}>{post.excerpt}</p>
            <div style={blogMeta}>
              <span>📅 {post.date}</span>
              <span>⏱️ {post.readTime}</span>
            </div>
            <div style={blogAuthor}>
              <span>✍️ {post.author}</span>
              <span style={authorRole}>{post.authorRole}</span>
            </div>
            <button style={readMoreBtn}>Read Full Article →</button>
          </div>
        ))}
      </div>

      {/* Newsletter Section */}
      <div style={newsletterSection}>
        <h2>Subscribe to Our Newsletter</h2>
        <p>Get the latest real estate insights delivered to your inbox</p>
        <div style={newsletterForm}>
          <input 
            type="email" 
            placeholder="Enter your email" 
            value={newsletterEmail}
            onChange={(e) => setNewsletterEmail(e.target.value)}
            style={newsletterInput} 
          />
          <button onClick={handleNewsletter} style={newsletterBtn}>Subscribe</button>
        </div>
        {newsletterMsg && <p style={{marginTop: 10, color: newsletterMsg.includes('✅') ? '#059669' : '#dc2626'}}>{newsletterMsg}</p>}
      </div>

      {/* Footer */}
      <div style={footerStyle}>
        <div style={footerContent}>
          <div>
            <h3>🏢 EstatesFlow</h3>
            <p>Complete Real Estate Management</p>
          </div>
          <div style={footerLinks}>
            <button onClick={() => window.location.href = '/'} style={footerLink}>Home</button>
            <button onClick={() => window.location.href = '/about'} style={footerLink}>About</button>
            <button onClick={() => window.location.href = '/blog'} style={footerLink}>Blog</button>
            <button onClick={() => window.location.href = '/#contact'} style={footerLink}>Contact</button>
          </div>
        </div>
        <p>© 2025 EstatesFlow CRM. All rights reserved.</p>
      </div>
    </div>
  )
}

const containerStyle = { fontFamily: "'Inter', sans-serif", color: '#1e293b' }
const heroStyle = { background: 'linear-gradient(135deg, #1e3a5f 0%, #0f2b42 100%)', color: '#fff', textAlign: 'center', padding: '60px 20px' }
const heroTitle = { fontSize: 48, marginBottom: 16 }
const heroSubtitle = { fontSize: 18, opacity: 0.9, maxWidth: 600, margin: '0 auto' }
const categoryBar = { background: '#fff', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 100 }
const categoryContainer = { display: 'flex', gap: 10, overflowX: 'auto', padding: '15px 20px', maxWidth: 1200, margin: '0 auto' }
const homeButtonContainer = { textAlign: 'center', marginTop: 20 }
const homeButton = { background: '#1e3a5f', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 14 }
const categoryBtn = { padding: '8px 20px', border: '1px solid #e2e8f0', borderRadius: 30, background: '#fff', cursor: 'pointer', fontSize: 14, whiteSpace: 'nowrap' }
const activeCategory = { padding: '8px 20px', border: 'none', borderRadius: 30, background: '#1e3a5f', color: '#fff', cursor: 'pointer', fontSize: 14, whiteSpace: 'nowrap' }
const blogGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 30, maxWidth: 1200, margin: '40px auto', padding: '0 20px' }
const blogCard = { background: '#fff', borderRadius: 20, overflow: 'hidden', border: '1px solid #e2e8f0', transition: 'transform 0.2s', cursor: 'pointer' }
const blogImage = { fontSize: 80, textAlign: 'center', padding: '30px', background: '#f8fafc' }
const blogCategory = { display: 'inline-block', background: '#e2e8f0', padding: '4px 12px', borderRadius: 20, fontSize: 12, margin: '15px 20px 10px' }
const blogTitle = { fontSize: 20, fontWeight: 700, margin: '0 20px 10px', color: '#1e3a5f' }
const blogExcerpt = { fontSize: 14, color: '#64748b', margin: '0 20px 15px', lineHeight: 1.5 }
const blogMeta = { display: 'flex', gap: 15, fontSize: 12, color: '#94a3b8', margin: '0 20px 10px' }
const blogAuthor = { display: 'flex', flexDirection: 'column', margin: '0 20px 15px', fontSize: 13, fontWeight: 500 }
const authorRole = { fontSize: 11, color: '#64748b', fontWeight: 'normal' }
const readMoreBtn = { margin: '0 20px 20px', background: 'none', border: 'none', color: '#2c7da0', cursor: 'pointer', fontWeight: 600, padding: 0 }
const newsletterSection = { textAlign: 'center', padding: '60px 20px', background: '#f8fafc' }
const newsletterForm = { display: 'flex', justifyContent: 'center', gap: 10, marginTop: 20, flexWrap: 'wrap' }
const newsletterInput = { padding: '12px 20px', border: '1px solid #e2e8f0', borderRadius: 30, width: '300px', fontSize: 14 }
const newsletterBtn = { padding: '12px 30px', background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 30, cursor: 'pointer', fontWeight: 600 }
const backButton = { margin: '20px', padding: '10px 20px', background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }
const articleContainer = { maxWidth: 800, margin: '0 auto', padding: '20px' }
const articleHeader = { textAlign: 'center', marginBottom: 40 }
const articleImage = { fontSize: 80, marginBottom: 20 }
const articleTitle = { fontSize: 36, marginBottom: 16, color: '#1e3a5f' }
const articleMeta = { display: 'flex', justifyContent: 'center', gap: 20, color: '#64748b', fontSize: 14 }
const articleContent = { lineHeight: 1.8, fontSize: 16, color: '#334155' }
const authorSection = { display: 'flex', gap: 20, marginTop: 40, padding: 20, background: '#f8fafc', borderRadius: 16, alignItems: 'center' }
const authorAvatar = { fontSize: 48 }
const authorInfo = { flex: 1 }
const authorBio = { fontSize: 14, color: '#64748b', marginTop: 8 }
const shareSection = { textAlign: 'center', marginTop: 40, padding: 20, borderTop: '1px solid #e2e8f0' }
const shareButtons = { display: 'flex', gap: 10, justifyContent: 'center', marginTop: 15, flexWrap: 'wrap' }
const shareBtn = { padding: '8px 16px', background: '#f1f5f9', border: 'none', borderRadius: 20, cursor: 'pointer' }
const relatedSection = { maxWidth: 800, margin: '40px auto', padding: '20px' }
const relatedGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20, marginTop: 20 }
const relatedCard = { padding: 20, border: '1px solid #e2e8f0', borderRadius: 16, cursor: 'pointer' }
const relatedImage = { fontSize: 40, marginBottom: 10 }
const footerStyle = { background: '#1e293b', color: '#fff', padding: '40px 20px', textAlign: 'center', marginTop: 40 }
const footerContent = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 20, maxWidth: 1200, margin: '0 auto 20px' }
const footerLinks = { display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }
const footerLink = { background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 14, textDecoration: 'none' }

export default Blog
